import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Typography, Grid2, useTheme } from '@mui/material';
import { useGetNcrClosedAgeingQuery } from "store/slices/apis/qualityDashboardSlice";
import { useParams } from "react-router-dom";
import styles from "styles/ProjectList.module.css";
import moment from 'moment';
import NcrPopup from './NcrPopup';
import { useState } from 'react';



const ClosedNCRGraph = () => {


    const delayRanges = [
        { label: "Less than 30 days", color: "#3E756D", className: styles.lowdot },
        { label: "31 to 60 days", color: "#FDCA41", className: styles.meddot },
        { label: "More than 60 days", color: "#E4533E", className: styles.hvacdot },
    ];

    const theme = useTheme();
    const { projectId } = useParams();
    const [popupData, setPopupData] = useState(null);
    const [open, setOpen] = useState(false);


    const { data, isLoading } = useGetNcrClosedAgeingQuery({ projectId });
    const delayData = data?.data || [];


    const tempDelayData = {};
    delayData.map(item => {
        tempDelayData[item?.range] = item;
    });

    const validDelayRanges = delayRanges.filter((item) => tempDelayData[item?.label])


    const analaysisData = validDelayRanges.map((item) => ({
        ...item,
        count: tempDelayData[item?.label].count,
        percentage: tempDelayData[item?.label].percentage,
    }))
    const totalCount = analaysisData.reduce((acc, item) => acc + item.count, 0);


    const chartOptions = {
        chart: {
            type: 'pie',
        },
        title: { text: null },
        plotOptions: {
            pie: {
                innerSize: '60%',
                borderRadius: 0,
                borderWidth: 0,
                borderColor: '#fff',
                dataLabels: { enabled: false },
                point: {
                    events: {
                        click: function () {
                            let delayRange = { delayDaysMin: 0, delayDaysMax: 30 };
                            switch (this.name) {
                                case "Less than 30 days":
                                    delayRange = { delayDaysMin: 0, delayDaysMax: 30 };
                                    break;
                                case "31 to 60 days":
                                    delayRange = { delayDaysMin: 31, delayDaysMax: 60 };
                                    break;
                                case "More than 60 days":
                                    delayRange = { delayDaysMin: 60, delayDaysMax: 1000 };
                                    break;
                            }
                            setPopupData({
                                name: this?.name,
                                value: delayRange,
                            });
                            setOpen(true);
                        }
                    }
                }
            },
        },
        colors: ['#3E756D', '#FFBF00', '#D64733'],

        series: [
            {
                name: 'Closed NCR',
                data: analaysisData.map((item) => ({
                    y: item?.percentage,
                    value: item?.percentage,
                    sliced: false,
                    selected: false,
                    name: item?.label
                })),
            },
        ],
        tooltip: {
            useHTML: true,
            formatter: function () {
                return `
      <b>${this.series.name}</b><br/>
      ${this.point.name}: <b>${this.point.y}%</b>
    `;
            },
        },

        credits: { enabled: false },
    };


    return (
        <>
            <Box sx={{ margin: '0 auto', p: 2 }}>
                <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}` }}>
                    Aging Analysis of Closed NCR Chart
                </Typography>
                <Typography sx={{ mt: "5px", color: "#5052588A", fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px', }}>
                    Control quality by analyzing the aging of closed NCRs to assess resolution efficiency and improve process effectiveness
                </Typography>
            </Box>
            {(!isLoading && totalCount === 0) ? (
                <Box
                    sx={{
                        width: '100%',
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        mt: 2
                    }}
                >
                    <Typography sx={{ fontSize: '16px', color: '#999' }}>
                        No data available to display the chart.
                    </Typography>
                </Box>
            ) : (
                <Grid2 container spacing={4} alignItems="center" justifyContent="center" sx={{}}>
                    <Grid2 xs={12} md={6}>
                        <Box sx={{ position: 'relative', width: '100%', maxWidth: 400, margin: '0 auto' }}>
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="body1" sx={{ fontWeight: 500, color: "#737373", fontSize: "14px" }}>
                                    Closed NCR
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: "#0A0A0A", fontSize: "24px", lineHeight: "28px" }}>
                                    {totalCount}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Right Side Labels */}
                    <Grid2 xs={12} md={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                            {analaysisData.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            backgroundColor: item.color,

                                        }}
                                    />
                                    <Typography variant="body1" sx={{
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '100%',
                                        letterSpacing: '-0.4px'
                                    }}>
                                        {item.label} :
                                    </Typography>
                                    <Typography sx={{
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '100%',
                                        color: "#182A4E",
                                        letterSpacing: '-0.4px'
                                    }}>
                                        {item.count}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid2>
                </Grid2>
            )}
            {open &&
                <NcrPopup title="Ageing Analysis of Closed NCR" value={popupData.value} status="ClosedAgeingAnalysis" onOpen={open} onClose={() => setOpen(false)} />
            }
        </>
    );
};


export default ClosedNCRGraph;
